"use client"

import { useState } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { Search, ChevronDown } from "lucide-react"
import Image from "@/src/components/Image"
import { AddEditAdminDialog } from "./components/add-edit-admin"

const sampleAdmins = [
  {
    id: 1,
    phone: "081-234-5678",
    name: "นายสมชาย ใจดี",
    email: "somchai@example.com",
    branch: "สาขาใหม่",
    usageDate: "01/10/2568",
    responseDate: "01/10/2568",
    status: "ใช้งาน",
  },
  {
    id: 2,
    phone: "082-345-6789",
    name: "นางสาวมานี รักดี",
    email: "manee@example.com",
    branch: "สาขา 1",
    usageDate: "15/09/2568",
    responseDate: "15/09/2568",
    status: "ใช้งาน",
  },
  {
    id: 3,
    phone: "083-456-7890",
    name: "นายวิชัย สุขใจ",
    email: "wichai@example.com",
    branch: "สาขา 2",
    usageDate: "20/09/2568",
    responseDate: "21/09/2568",
    status: "รอตอบรับ",
  },
]

export default function AdminManagePage() {
  const [isAddAdminDialogOpen, setIsAddAdminDialogOpen] = useState(false)

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <h1 className="text-2xl font-bold text-gray-900">จัดการแอดมิน</h1>

        {/* Branch Tabs */}
        <TabGroup defaultValue="admin" color="brand-blue" fullWidth>
          <TabGroupList>
            <TabGroupTrigger value="admin">แอดมินร้านค้า</TabGroupTrigger>
            <TabGroupTrigger value="history">ประวัติการเชิญ</TabGroupTrigger>
          </TabGroupList>
        </TabGroup>

        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Search Bar */}
          <div className="flex gap-2 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input type="text" placeholder="ชื่อแอดมิน, เบอร์โทร" className="pl-10 h-11" />
            </div>
            <Button variant="secondary" className="bg-gray-400 hover:bg-gray-500 text-white px-6 h-11">
              ค้นหา
            </Button>
          </div>

          {/* Branch Filter */}
          <div className="flex items-center gap-3">
            <span className="text-gray-700 font-medium">สาขา</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 h-11 px-4 bg-transparent min-w-[140px]">
                  <Image src="/icons/icon-branch.svg" alt="Branch" width={18} height={16} className="flex-shrink-0" />
                  <span>ทั้งหมด</span>
                  <ChevronDown className="h-4 w-4 ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem>ทั้งหมด</DropdownMenuItem>
                <DropdownMenuItem>สาขาใหม่</DropdownMenuItem>
                <DropdownMenuItem>สาขา 1</DropdownMenuItem>
                <DropdownMenuItem>สาขา 2</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Header with Count and Add Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-normal text-gray-900">
              รายชื่อ <span className="text-brand-blue">แอดมินร้านค้า ({sampleAdmins.length}/10)</span>
            </h2>
            <Button
              variant="outline"
              className="border-2 border-brand-blue text-brand-blue hover:bg-brand-blue/5 bg-transparent"
              onClick={() => setIsAddAdminDialogOpen(true)}
            >
              เพิ่มแอดมิน
            </Button>
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-gray-700">เบอร์/ชื่อ-สกุล/อีเมล</TableHead>
                <TableHead className="text-gray-700">สาขา</TableHead>
                <TableHead className="text-gray-700">วันที่เชิย</TableHead>
                <TableHead className="text-gray-700">วันที่ตอบรับ</TableHead>
                <TableHead className="text-gray-700">สถานะ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sampleAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{admin.phone}</span>
                      <span className="text-sm text-gray-600">{admin.name}</span>
                      <span className="text-sm text-gray-500">{admin.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>{admin.branch}</TableCell>
                  <TableCell>{admin.usageDate}</TableCell>
                  <TableCell>{admin.responseDate}</TableCell>
                  <TableCell>
                    <span
                      className={
                        admin.status === "ใช้งาน" ? "text-brand-primary font-medium" : "text-orange-500 font-medium"
                      }
                    >
                      {admin.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Admin Dialog */}
      <AddEditAdminDialog
        open={isAddAdminDialogOpen}
        onOpenChange={setIsAddAdminDialogOpen}
        mode="add"
      />
    </DashboardLayout>
  )
}
